import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Copy, Heading2, Image, Link2, Minus, MousePointerClick, Package, Plus, Save, Send, Trash2, Type } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errMsg, fmtDate } from "../lib/api";
import { Card, Field, ImageInput, PageTitle, useList } from "./ui";

const THEMES = [["sage", "Šalvěj", "#A3AC86"], ["daisy", "Kopretina", "#E0B127"], ["terra", "Terakota", "#D96B43"], ["olive", "Olivová", "#2D3426"], ["blush", "Růžová", "#D9A3A0"]];
const BLOCKS = [["text", "Text", Type], ["heading", "Nadpis", Heading2], ["product", "Produkt", Package], ["button", "Tlačítko", MousePointerClick], ["image", "Obrázek", Image], ["divider", "Oddělovač", Minus]];
const STATUS = { draft: "Koncept", sending: "Odesílá se…", sent: "Odesláno" };
const origin = window.location.origin;

const TextBlock = ({ b, set, products, i }) => {
  const [pid, setPid] = useState("");
  const insert = () => {
    const p = products.find((x) => x.id === pid);
    if (p) set({ text: `${b.text || ""}${b.text ? " " : ""}[${p.name}](${origin}/produkt/${p.slug})` });
  };
  return (
    <div className="space-y-2">
      <Field label="Text (**tučně**, [text odkazu](https://…))" textarea rows={5} value={b.text || ""} onChange={(e) => set({ text: e.target.value })} testid={`nl-block-text-${i}`} />
      <div className="flex flex-wrap gap-2">
        <select className="input-soft !w-auto flex-1" value={pid} onChange={(e) => setPid(e.target.value)} data-testid={`nl-block-link-product-${i}`}><option value="">Vyberte produkt pro odkaz…</option>{products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
        <button type="button" className="btn-ghost !px-4 !py-2" onClick={insert} disabled={!pid} data-testid={`nl-block-insert-link-${i}`}><Link2 className="h-4 w-4" /> Vložit odkaz</button>
      </div>
    </div>
  );
};

const BlockBody = ({ b, set, products, i }) => {
  if (b.type === "text") return <TextBlock b={b} set={set} products={products} i={i} />;
  if (b.type === "heading") return <Field label="Nadpis" value={b.text || ""} onChange={(e) => set({ text: e.target.value })} testid={`nl-block-heading-${i}`} />;
  if (b.type === "product") return (
    <select className="input-soft" value={b.product_id || ""} onChange={(e) => set({ product_id: e.target.value })} data-testid={`nl-block-product-${i}`}>
      <option value="">Vyberte produkt…</option>{products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
    </select>
  );
  if (b.type === "button") return (
    <div className="grid gap-2 sm:grid-cols-2">
      <Field label="Text tlačítka" value={b.label || ""} onChange={(e) => set({ label: e.target.value })} testid={`nl-block-btn-label-${i}`} />
      <Field label="Odkaz (/eshop nebo https://…)" value={b.url || ""} onChange={(e) => set({ url: e.target.value })} testid={`nl-block-btn-url-${i}`} />
    </div>
  );
  if (b.type === "image") return (
    <div className="grid gap-2 sm:grid-cols-[180px_1fr]">
      <ImageInput value={b.path} onChange={(v) => set({ path: v })} className="h-28 w-full" testid={`nl-block-image-${i}`} />
      <Field label="Popisek pod obrázkem" value={b.caption || ""} onChange={(e) => set({ caption: e.target.value })} testid={`nl-block-caption-${i}`} />
    </div>
  );
  return <p className="text-xs text-[#6E7565]">Ozdobný oddělovač s kopretinami</p>;
};

const BlocksEditor = ({ blocks, onChange, products, disabled }) => {
  const upd = (i, v) => onChange(blocks.map((b, k) => (k === i ? { ...b, ...v } : b)));
  const move = (i, d) => { const n = [...blocks]; [n[i], n[i + d]] = [n[i + d], n[i]]; onChange(n); };
  return (
    <fieldset disabled={disabled} className="space-y-3">
      {blocks.map((b, i) => (
        <div key={i} className="rounded-2xl border bg-[#FFFDF8] p-3" data-testid={`nl-block-${i}`}>
          <div className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-[#6E7565]">
            <span className="flex-1">{BLOCKS.find((x) => x[0] === b.type)?.[1]}</span>
            {i > 0 && <button type="button" onClick={() => move(i, -1)} className="rounded-full p-1 hover:bg-[#E9EFE0]" data-testid={`nl-block-up-${i}`}><ArrowUp className="h-4 w-4" /></button>}
            {i < blocks.length - 1 && <button type="button" onClick={() => move(i, 1)} className="rounded-full p-1 hover:bg-[#E9EFE0]" data-testid={`nl-block-down-${i}`}><ArrowDown className="h-4 w-4" /></button>}
            <button type="button" onClick={() => onChange(blocks.filter((_, k) => k !== i))} className="rounded-full p-1 text-[#D96B43] hover:bg-[#F8E1D7]" data-testid={`nl-block-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
          <BlockBody b={b} i={i} set={(v) => upd(i, v)} products={products} />
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        {BLOCKS.map(([t, l, I]) => <button type="button" key={t} onClick={() => onChange([...blocks, { type: t }])} className="flex items-center gap-1.5 rounded-full border bg-white px-3 py-2 text-xs font-semibold hover:bg-[#E9EFE0]" data-testid={`nl-add-${t}`}><Plus className="h-3 w-3" /><I className="h-3.5 w-3.5" /> {l}</button>)}
      </div>
    </fieldset>
  );
};

const Preview = ({ c }) => {
  const [html, setHtml] = useState("");
  const [mobile, setMobile] = useState(false);
  const t = useRef();
  useEffect(() => {
    clearTimeout(t.current);
    t.current = setTimeout(() => adminApi.post("/campaigns/preview", { ...c, origin_url: origin }).then((r) => setHtml(r.data.html)).catch(() => {}), 500);
  }, [c]);
  return (
    <Card title="Náhled e-mailu" action={<div className="flex gap-1 rounded-full bg-[#F7F3EA] p-1 text-xs">{[["Počítač", false], ["Mobil", true]].map(([l, m]) => <button key={l} onClick={() => setMobile(m)} className={`rounded-full px-3 py-1 ${mobile === m ? "bg-[#7A8460] text-white" : ""}`} data-testid={`nl-preview-${m ? "mobile" : "desktop"}`}>{l}</button>)}</div>}>
      <p className="mb-2 truncate text-sm"><span className="text-[#6E7565]">Předmět:</span> <b data-testid="nl-preview-subject">{c.subject || "–"}</b></p>
      <div className="grid place-items-center rounded-2xl bg-[#EFEBE1] p-2">
        <iframe title="Náhled newsletteru" srcDoc={html} className="h-[75vh] rounded-xl bg-white transition-all" style={{ width: mobile ? 390 : "100%", maxWidth: "100%" }} data-testid="nl-preview-frame" />
      </div>
    </Card>
  );
};

const Composer = ({ c, setC, products, reload, subs }) => {
  const [testTo, setTestTo] = useState("");
  const draft = c.status === "draft";
  const set = (k, v) => setC({ ...c, [k]: v });
  const save = async (quiet) => {
    const { data } = await adminApi.put(`/campaigns/${c.id}`, c);
    if (!quiet) toast.success("Koncept uložen");
    reload();
    return data;
  };
  const run = (fn) => fn().catch((e) => toast.error(errMsg(e)));
  const test = () => run(async () => {
    await save(true);
    const { data } = await adminApi.post(`/campaigns/${c.id}/test`, { origin_url: origin, test_email: testTo || null });
    data.sent ? toast.success(`Testovací e-mail odeslán na ${data.to}`) : toast.error(`Odeslání selhalo: ${data.error || ""}`);
  });
  const sendAll = () => run(async () => {
    if (!window.confirm(`Opravdu odeslat newsletter ${subs} odběratelům? Tuto akci nelze vrátit.`)) return;
    await save(true);
    const { data } = await adminApi.post(`/campaigns/${c.id}/send`, { origin_url: origin });
    toast.success(`Odesílání spuštěno – ${data.recipients} příjemců`);
    reload();
    setC({ ...c, status: "sending" });
  });
  return (
    <div className="space-y-4">
      <Card title="Obsah" action={draft && <button className="btn-sage !px-4 !py-2" onClick={() => run(() => save())} data-testid="nl-save-btn"><Save className="h-4 w-4" /> Uložit</button>}>
        <fieldset disabled={!draft} className="space-y-3">
          <Field label="Předmět e-mailu *" value={c.subject} onChange={(e) => set("subject", e.target.value)} testid="nl-subject" />
          <Field label="Náhledový text (zobrazí se v poště za předmětem)" value={c.preheader} onChange={(e) => set("preheader", e.target.value)} testid="nl-preheader" />
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Nadtitulek" value={c.eyebrow} onChange={(e) => set("eyebrow", e.target.value)} testid="nl-eyebrow" />
            <Field label="Hlavní nadpis" value={c.title} onChange={(e) => set("title", e.target.value)} testid="nl-title" />
          </div>
          <div><p className="mb-1 text-xs font-semibold uppercase tracking-wider text-[#6E7565]">Barevné téma</p>
            <div className="flex flex-wrap gap-2">{THEMES.map(([k, l, col]) => <button type="button" key={k} onClick={() => set("theme", k)} className={`flex items-center gap-2 rounded-full border-2 px-3 py-1.5 text-xs font-semibold ${c.theme === k ? "border-[#2D3426]" : "border-transparent bg-white"}`} data-testid={`nl-theme-${k}`}><span className="h-4 w-4 rounded-full" style={{ background: col }} /> {l}</button>)}</div>
          </div>
        </fieldset>
      </Card>
      <Card title="Bloky obsahu"><BlocksEditor blocks={c.blocks || []} onChange={(v) => set("blocks", v)} products={products} disabled={!draft} /></Card>
      {draft ? (
        <Card title="Odeslání">
          <div className="flex flex-wrap gap-2">
            <input className="input-soft !w-auto flex-1" type="email" placeholder="Testovací e-mail (prázdné = váš e-mail)" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="nl-test-email" />
            <button className="btn-ghost" onClick={test} data-testid="nl-test-btn"><Send className="h-4 w-4" /> Poslat test</button>
          </div>
          <button className="btn-sage mt-4 w-full" onClick={sendAll} disabled={!subs} data-testid="nl-send-all-btn"><Send className="h-4 w-4" /> Odeslat všem odběratelům ({subs})</button>
          <p className="mt-2 text-xs text-[#6E7565]">Odesílá se jen přihlášeným odběratelům. Každý e-mail obsahuje odkaz pro odhlášení.</p>
        </Card>
      ) : <Card><p className="text-sm" data-testid="nl-sent-info">{STATUS[c.status]} · odesláno {c.sent_count || 0} z {c.recipients || 0}{c.failed_count ? ` · chyby ${c.failed_count}` : ""}{c.sent_at ? ` · ${fmtDate(c.sent_at)}` : ""}</p></Card>}
    </div>
  );
};

const Subscribers = ({ list, reload }) => {
  const remove = async (email) => {
    if (!window.confirm(`Smazat odběratele ${email}?`)) return;
    await adminApi.delete(`/newsletter/${encodeURIComponent(email)}`);
    reload();
  };
  return (
    <Card title={`Odběratelé (${list.filter((n) => !n.unsubscribed).length} aktivních)`}>
      <div className="max-h-[70vh] divide-y overflow-y-auto">
        {list.map((n) => (
          <div key={n.email} className="flex items-center gap-3 py-2 text-sm" data-testid="nl-subscriber-row">
            <span className="flex-1 truncate">{n.email}</span>
            <span className={`rounded-full px-2 text-xs ${n.unsubscribed ? "bg-[#F8E1D7]" : "bg-[#E9EFE0]"}`}>{n.unsubscribed ? "odhlášen" : "aktivní"}</span>
            <span className="text-xs text-[#6E7565]">{fmtDate(n.created_at)}</span>
            <button onClick={() => remove(n.email)} className="rounded-full p-1 text-[#D96B43] hover:bg-[#F8E1D7]"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {!list.length && <p className="py-4 text-sm text-[#6E7565]">Zatím žádní odběratelé.</p>}
      </div>
    </Card>
  );
};

export default function Newsletter() {
  const [list, reload] = useList("/campaigns");
  const [subsList, reloadSubs] = useList("/newsletter");
  const [products] = useList("/products");
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("campaigns");
  const subs = (subsList || []).filter((n) => !n.unsubscribed).length;
  const create = (base = {}) => adminApi.post("/campaigns", base).then((r) => { setC(r.data); reload(); }).catch((e) => toast.error(errMsg(e)));
  const remove = async (x) => {
    if (!window.confirm("Smazat newsletter?")) return;
    await adminApi.delete(`/campaigns/${x.id}`);
    if (c?.id === x.id) setC(null);
    reload();
  };
  return (
    <div data-testid="admin-newsletter">
      <PageTitle title="Newsletter" sub="Napište, nastylujte a rozešlete novinky přihlášeným odběratelům" action={<button className="btn-sage" onClick={() => create({ blocks: [{ type: "text", text: "" }] })} data-testid="nl-new-btn"><Plus className="h-4 w-4" /> Nový newsletter</button>} />
      <div className="mb-5 flex gap-2">{[["campaigns", "Newslettery"], ["subs", `Odběratelé (${subs})`]].map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`rounded-full px-4 py-2 text-sm ${tab === k ? "bg-[#7A8460] text-white" : "border bg-white"}`} data-testid={`nl-tab-${k}`}>{l}</button>)}</div>
      {tab === "subs" ? <Subscribers list={subsList || []} reload={reloadSubs} /> : (
        <>
          <div className="no-scrollbar mb-5 flex gap-2 overflow-x-auto pb-1">
            {(list || []).map((x) => (
              <div key={x.id} className={`flex shrink-0 items-center gap-2 rounded-2xl border px-3 py-2 text-sm ${c?.id === x.id ? "border-[#7A8460] bg-[#E9EFE0]" : "bg-white"}`} data-testid="nl-campaign-item">
                <button onClick={() => setC(x)} className="text-left"><b className="block max-w-[200px] truncate">{x.subject || "Bez předmětu"}</b><span className="text-xs text-[#6E7565]">{STATUS[x.status]} · {fmtDate(x.sent_at || x.created_at)}</span></button>
                <button title="Kopie" onClick={() => create({ ...x, subject: `${x.subject} (kopie)` })} className="rounded-full p-1 hover:bg-white" data-testid="nl-campaign-copy"><Copy className="h-4 w-4" /></button>
                <button title="Smazat" onClick={() => remove(x)} className="rounded-full p-1 text-[#D96B43] hover:bg-[#F8E1D7]" data-testid="nl-campaign-delete"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          {c ? (
            <div className="grid gap-6 2xl:grid-cols-2">
              <Composer key={c.id} c={c} setC={setC} products={products || []} reload={reload} subs={subs} />
              <div className="2xl:sticky 2xl:top-6 2xl:h-fit"><Preview c={c} /></div>
            </div>
          ) : <p className="rounded-3xl border bg-white p-10 text-center text-[#6E7565]" data-testid="nl-empty">Vyberte newsletter výše nebo vytvořte nový.</p>}
        </>
      )}
    </div>
  );
}
